/**
 * Picking the color a grade is shown in.
 *
 * The control is a swatch that opens the school's palette, not a color wheel:
 * the choice is "which of our colors is this grade", which is a short list, and
 * a list can be scanned and compared in a way a gradient square cannot.
 */
import { useState } from 'react';
import { Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { FALLBACK_GRADE_COLOR, GRADE_COLORS, gradeColorName } from './gradeColors';

interface GradeColorPickerProps {
  value?: string | null;
  onChange: (hex: string) => void;
  /** Named in the trigger and the options so each row is distinguishable. */
  label: string;
}

export function GradeColorPicker({ value, onChange, label }: GradeColorPickerProps) {
  const [open, setOpen] = useState(false);
  const current = value || FALLBACK_GRADE_COLOR;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Color for ${label}: ${gradeColorName(value)}`}
          className="flex h-9 items-center gap-2 rounded-md border bg-background px-2 text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span
            aria-hidden
            className="h-5 w-5 shrink-0 rounded"
            style={{ backgroundColor: current }}
          />
          <span className="text-muted-foreground">{gradeColorName(value)}</span>
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-auto p-2">
        <div className="grid grid-cols-5 gap-1">
          {GRADE_COLORS.map((color) => {
            const selected = current.toLowerCase() === color.hex.toLowerCase();
            return (
              <button
                key={color.hex}
                type="button"
                aria-label={color.name}
                aria-pressed={selected}
                title={color.name}
                onClick={() => {
                  onChange(color.hex);
                  setOpen(false);
                }}
                className="flex h-8 w-8 items-center justify-center rounded transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                style={{ backgroundColor: color.hex }}
              >
                {selected && <Check className="h-4 w-4 text-white" aria-hidden />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default GradeColorPicker;
